import React, { useState, useRef } from 'react';
import { Plus, Trash2, Settings, Download, Eye, EyeOff, X, HelpCircle, FileText, Image, Table2, BookOpen, Columns, Rows, Upload, List, ListOrdered } from 'lucide-react';

export default function DocumentGenerator() {
  const [docData, setDocData] = useState({
    title: 'IoT-BASED WATER LEVEL MONITORING SYSTEM',
    subtitle: 'PROJECT THESIS',
    collegeName: '',
    department: 'EE',
    studentName: '',
    guideName: '',
    year: new Date().getFullYear(),
  });

  // Define the required chapters as per guidelines
  const requiredChapters = [
    { id: 'declaration', type: 'declaration', title: 'CANDIDATE\'S DECLARATION', content: '' },
    { id: 'certificate', type: 'certificate', title: 'CERTIFICATE', content: '' },
    { id: 'acknowledgement', type: 'acknowledgement', title: 'ACKNOWLEDGEMENT', content: '' },
    { id: 'abstract', type: 'abstract', title: 'ABSTRACT', content: '' },
    { id: 'chapter1', type: 'chapter1', title: 'INTRODUCTION', content: '' },
    { id: 'chapter2', type: 'chapter2', title: 'LITERATURE REVIEW', content: '' },
    { id: 'chapter3', type: 'chapter3', title: 'AIM & OBJECTIVE', content: '' },
    { id: 'chapter4', type: 'chapter4', title: 'PROPOSED METHODOLOGY', content: '' },
    { id: 'chapter5', type: 'chapter5', title: 'CONCEPTUAL DESIGN', content: '' },
    { id: 'chapter6', type: 'chapter6', title: 'SOFTWARE/HARDWARE', content: '' },
    { id: 'chapter7', type: 'chapter7', title: 'IMPLEMENTATION/WORKING', content: '' },
    { id: 'chapter8', type: 'chapter8', title: 'COST ANALYSIS', content: '' },
    { id: 'chapter9', type: 'chapter9', title: 'RESULT ANALYSIS AND DISCUSSION', content: '' },
    { id: 'chapter10', type: 'chapter10', title: 'ADVANTAGES AND DISADVANTAGES/APPLICATION', content: '' },
    { id: 'conclusion', type: 'conclusion', title: 'CONCLUSION', content: '' },
    { id: 'futureScope', type: 'futureScope', title: 'FUTURE SCOPE', content: '' },
    { id: 'references', type: 'references', title: 'REFERENCES', content: '' },
    { id: 'publications', type: 'publications', title: 'LIST OF PUBLICATIONS', content: '' },
    { id: 'annexure', type: 'annexure', title: 'ANNEXURE', content: '' },
    { id: 'participation', type: 'participation', title: 'CERTIFICATES OF PARTICIPATION', content: '' }
  ];

  const [sections, setSections] = useState(requiredChapters.map((chapter, index) => ({
    ...chapter,
    id: index + 1,
    customTitle: '',
    tableData: [],
    images: [],
    bulletPoints: [],
    subsections: []
  })));

  const [figures, setFigures] = useState([]);
  const [tables, setTables] = useState([]);
  const [abbreviations, setAbbreviations] = useState([]);

  const [settings, setSettings] = useState({
    marginTop: 25.4, // 1 inch in mm
    marginBottom: 31.75, // 1.25 inch in mm
    marginLeft: 38.1, // 1.5 inch in mm
    marginRight: 25.4, // 1 inch in mm
    fontSize: 12,
    fontFamily: 'Times New Roman',
    includePageNumbers: true,
    includeIndex: true,
    includeFigureList: true,
    includeTableList: true,
    includeAbbreviations: true,
    lineSpacing: 1.5
  });

  const [nextId, setNextId] = useState(sections.length + 1);
  const [showSettings, setShowSettings] = useState(false);
  const [preview, setPreview] = useState(true);
  const [activeTab, setActiveTab] = useState('sections');
  const [showHelpTooltip, setShowHelpTooltip] = useState(true);
  
  const fileInputRefs = useRef({});

  const collegeTemplates = [
    { name: 'Custom', value: 'custom' },
    { name: 'MIT - Maharashtra Institute of Technology', value: 'mit' },
    { name: 'COEP - College of Engineering Pune', value: 'coep' },
    { name: 'VIT - Vishwakarma Institute of Technology', value: 'vit' },
    { name: 'PICT - Pune Institute of Computer Technology', value: 'pict' },
    { name: 'IIT Bombay', value: 'iitb' },
    { name: 'VJTI - Veermata Jijabai Technological Institute', value: 'vjti' },
  ];

  // Automatic numbering functions
  const getChapterNumber = (sectionType) => {
    if (sectionType.startsWith('chapter')) {
      return sectionType.replace('chapter', '');
    }
    return null;
  };

  const getFigureNumber = (sectionId, imageIndex) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return '1.1';
    
    const chapterNum = getChapterNumber(section.type);
    if (chapterNum) {
      const chapterFigures = sections
        .filter(s => getChapterNumber(s.type) === chapterNum)
        .flatMap(s => s.images || []);
      
      const currentFigureIndex = chapterFigures.findIndex(fig => 
        fig.sectionId === sectionId && fig.index === imageIndex
      );
      
      return `${chapterNum}.${currentFigureIndex + 1}`;
    } else {
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      return `0.${sectionIndex + 1}.${imageIndex + 1}`;
    }
  };

  const getTableNumber = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return '1';
    
    const chapterNum = getChapterNumber(section.type);
    if (chapterNum) {
      const chapterTables = sections
        .filter(s => getChapterNumber(s.type) === chapterNum)
        .filter(s => s.tableData && s.tableData.length > 0);
      
      const currentTableIndex = chapterTables.findIndex(s => s.id === sectionId);
      return `${chapterNum}.${currentTableIndex + 1}`;
    } else {
      const sectionIndex = sections.findIndex(s => s.id === sectionId);
      return `0.${sectionIndex + 1}`;
    }
  };

  // Update figures list automatically
  const updateFiguresList = () => {
    const allFigures = sections.flatMap(section => 
      (section.images || []).map((image, index) => ({
        id: `${section.id}-${index}`,
        number: getFigureNumber(section.id, index),
        name: image.name || `Figure ${getFigureNumber(section.id, index)}`,
        page: calculatePageNumbers().sectionPages[section.id] || 1
      }))
    );
    setFigures(allFigures);
  };

  // Update tables list automatically
  const updateTablesList = () => {
    const allTables = sections
      .filter(section => section.tableData && section.tableData.length > 0)
      .map(section => ({
        id: section.id,
        number: getTableNumber(section.id),
        name: section.tableName || `Table ${getTableNumber(section.id)}`,
        page: calculatePageNumbers().sectionPages[section.id] || 1
      }));
    setTables(allTables);
  };

  // Enhanced table functions
  const addTableRow = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const currentTable = section.tableData || [];
        const newRow = currentTable.length > 0 
          ? Array(currentTable[0].length).fill('') 
          : [''];
        return { ...section, tableData: [...currentTable, newRow] };
      }
      return section;
    }));
  };

  const removeTableRow = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && section.tableData && section.tableData.length > 1) {
        return { ...section, tableData: section.tableData.slice(0, -1) };
      }
      return section;
    }));
  };

  const addTableColumn = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const currentTable = section.tableData || [];
        const newTable = currentTable.map(row => [...row, '']);
        return { ...section, tableData: newTable };
      }
      return section;
    }));
  };

  const removeTableColumn = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId && section.tableData && section.tableData.length > 0) {
        const newTable = section.tableData.map(row => row.slice(0, -1));
        return { ...section, tableData: newTable };
      }
      return section;
    }));
  };

  const updateTableCell = (sectionId, rowIndex, colIndex, value) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const newTable = [...section.tableData];
        newTable[rowIndex][colIndex] = value;
        return { ...section, tableData: newTable };
      }
      return section;
    }));
  };

  const clearTable = (sectionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return { ...section, tableData: [] };
      }
      return section;
    }));
  };

  // Image handling functions
  const handleImageUpload = (sectionId, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now(),
          url: e.target.result,
          name: `Figure ${getFigureNumber(sectionId, (sections.find(s => s.id === sectionId)?.images || []).length)}`,
          sectionId: sectionId,
          index: (sections.find(s => s.id === sectionId)?.images || []).length
        };
        
        setSections(sections.map(section => 
          section.id === sectionId 
            ? { ...section, images: [...(section.images || []), newImage] }
            : section
        ));
      };
      reader.readAsDataURL(file);
    }
  };

  const updateImageName = (sectionId, imageId, newName) => {
    setSections(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            images: (section.images || []).map(img => 
              img.id === imageId ? { ...img, name: newName } : img
            )
          }
        : section
    ));
  };

  const deleteImage = (sectionId, imageId) => {
    setSections(sections.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            images: (section.images || []).filter(img => img.id !== imageId)
          }
        : section
    ));
  };

  // Bullet point functions
  const addBulletPoint = (sectionId, type = 'bullet') => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const currentBullets = section.bulletPoints || [];
        const newBullet = {
          id: Date.now(),
          type: type,
          text: '',
          level: 0
        };
        return { ...section, bulletPoints: [...currentBullets, newBullet] };
      }
      return section;
    }));
  };

  const updateBulletPoint = (sectionId, bulletId, field, value) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          bulletPoints: (section.bulletPoints || []).map(bullet =>
            bullet.id === bulletId ? { ...bullet, [field]: value } : bullet
          )
        };
      }
      return section;
    }));
  };

  const deleteBulletPoint = (sectionId, bulletId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          bulletPoints: (section.bulletPoints || []).filter(bullet => bullet.id !== bulletId)
        };
      }
      return section;
    }));
  };

  const changeBulletLevel = (sectionId, bulletId, direction) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          bulletPoints: (section.bulletPoints || []).map(bullet => {
            if (bullet.id === bulletId) {
              const newLevel = direction === 'increase' ? bullet.level + 1 : Math.max(0, bullet.level - 1);
              return { ...bullet, level: newLevel };
            }
            return bullet;
          })
        };
      }
      return section;
    }));
  };

  // Automatic numbering for bullet points
  const getBulletNumber = (bulletPoints, index) => {
    if (index === 0) return '1';
    
    const current = bulletPoints[index];
    const previous = bulletPoints[index - 1];
    
    if (current.level === previous.level) {
      const prevNumber = previous.number || '1';
      const lastDot = prevNumber.lastIndexOf('.');
      if (lastDot === -1) {
        return (parseInt(prevNumber) + 1).toString();
      } else {
        const base = prevNumber.substring(0, lastDot + 1);
        const lastNum = parseInt(prevNumber.substring(lastDot + 1)) + 1;
        return base + lastNum;
      }
    } else if (current.level > previous.level) {
      return (previous.number || '1') + '.1';
    } else {
      let parentIndex = index - 1;
      while (parentIndex >= 0 && bulletPoints[parentIndex].level > current.level) {
        parentIndex--;
      }
      if (parentIndex >= 0) {
        const parentNumber = bulletPoints[parentIndex].number || '1';
        return parentNumber + '.1';
      }
      return '1';
    }
  };

  const calculateBulletNumbers = (bulletPoints) => {
    if (!bulletPoints || bulletPoints.length === 0) return [];
    
    return bulletPoints.map((bullet, index) => {
      if (bullet.type === 'number') {
        let number = '1';
        if (index > 0) {
          number = getBulletNumber(bulletPoints, index);
        }
        return { ...bullet, number };
      }
      return bullet;
    });
  };

  // Automatic page numbering calculation
  const calculatePageNumbers = () => {
    let currentPage = 1; // Cover page
    
    const listPages = {};
    const sectionPages = {};

    // Count pages for preliminary sections (no page numbers shown but counted)
    const preliminarySections = ['declaration', 'certificate', 'acknowledgement'];
    let preliminaryPageCount = 0;
    
    sections.forEach(section => {
      if (preliminarySections.includes(section.type)) {
        preliminaryPageCount++;
      }
    });

    currentPage += preliminaryPageCount;

    // Abstract starts with page numbers
    const abstractSection = sections.find(s => s.type === 'abstract');
    if (abstractSection) {
      sectionPages[abstractSection.id] = currentPage;
      currentPage++;
    }

    // Lists
    if (settings.includeFigureList && figures.length > 0) {
      listPages.figures = currentPage;
      currentPage++;
    }
    
    if (settings.includeTableList && tables.length > 0) {
      listPages.tables = currentPage;
      currentPage++;
    }
    
    if (settings.includeAbbreviations && abbreviations.length > 0) {
      listPages.abbreviations = currentPage;
      currentPage++;
    }

    if (settings.includeIndex) {
      listPages.index = currentPage;
      currentPage++;
    }

    // Content sections with proper numbering
    sections.forEach(section => {
      if (!preliminarySections.includes(section.type) && section.type !== 'abstract') {
        sectionPages[section.id] = currentPage;
        currentPage++;
      }
    });

    return { listPages, sectionPages, totalPages: currentPage };
  };

  const updateSection = (id, field, value) => {
    setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const addAbbreviation = () => {
    setAbbreviations([...abbreviations, { 
      id: Date.now(), 
      abbreviation: '', 
      meaning: '' 
    }]);
  };

  const updateAbbreviation = (id, field, value) => {
    setAbbreviations(abbreviations.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const deleteAbbreviation = (id) => {
    setAbbreviations(abbreviations.filter(a => a.id !== id));
  };

  // Enhanced index generation with automatic page numbers
  const generateIndexHtml = () => {
    const { listPages, sectionPages } = calculatePageNumbers();
    const indexRows = [];
    
    // Add list entries
    if (settings.includeFigureList && figures.length > 0) {
      indexRows.push(`<tr><td></td><td></td><td></td><td><b>List of Figures</b></td><td class="text-right">${listPages.figures}</td></tr>`);
    }
    if (settings.includeTableList && tables.length > 0) {
      indexRows.push(`<tr><td></td><td></td><td></td><td><b>List of Tables</b></td><td class="text-right">${listPages.tables}</td></tr>`);
    }
    if (settings.includeAbbreviations && abbreviations.length > 0) {
      indexRows.push(`<tr><td></td><td></td><td></td><td><b>List of Abbreviations</b></td><td class="text-right">${listPages.abbreviations}</td></tr>`);
    }
    
    // Add section entries
    sections.forEach((section) => {
      if (section.type.startsWith('chapter')) {
        const chapterNum = section.type.replace('chapter', '');
        indexRows.push(`<tr><td style="padding:4mm;font-weight:bold;">${chapterNum}</td><td style="padding:4mm;"></td><td style="padding:4mm;"></td><td style="padding:4mm;font-weight:bold;">${section.title}</td><td style="padding:4mm;text-align:right;">${sectionPages[section.id]}</td></tr>`);
      } else if (section.type === 'abstract') {
        indexRows.push(`<tr><td></td><td></td><td></td><td><b>Abstract</b></td><td class="text-right">${sectionPages[section.id]}</td></tr>`);
      }
    });
    
    return indexRows.join('');
  };

  const handlePrint = () => {
    updateFiguresList();
    updateTablesList();
    const { listPages, sectionPages } = calculatePageNumbers();
    
    let htmlContent = `<!DOCTYPE html><html><head><style>
      * { margin:0; padding:0; box-sizing:border-box; } 
      body { font-family:'Times New Roman'; line-height:1.5; } 
      .page { width:210mm; min-height:297mm; padding:${settings.marginTop}mm ${settings.marginRight}mm ${settings.marginBottom}mm ${settings.marginLeft}mm; page-break-after:always; position:relative; background:white; font-size:12pt; } 
      .title-page { display:flex; flex-direction:column; justify-content:center; align-items:center; text-align:center; min-height:250mm; }
      .title { font-size:16pt; font-weight:bold; margin:20mm 0; text-transform:uppercase; } 
      .subtitle { font-size:14pt; margin:10mm 0; text-transform:uppercase; } 
      .college-info { font-size:12pt; margin:5mm 0; }
      .list-title { font-size:16pt; font-weight:bold; text-align:center; text-decoration:underline; margin:10mm 0; text-transform:uppercase; } 
      table { width:100%; border-collapse:collapse; margin:10mm 0; } 
      table, th, td { border:1px solid #000; }
      th, td { padding:3mm; text-align:left; font-size:12pt; }
      .text-right { text-align:right; }
      .content-table { margin:10mm 0; width:100%; }
      .content-image { max-width:80%; margin:10mm auto; display:block; text-align:center; }
      .image-caption { text-align:center; font-style:italic; margin-top:2mm; font-size:12pt; font-weight:bold; }
      .page-number { position:absolute; bottom:10mm; right:10mm; font-size:10pt; }
      .department-name { position:absolute; bottom:10mm; left:10mm; font-size:10pt; }
      .bullet-points { margin:5mm 0; }
      .bullet-item { margin:2mm 0; font-size:12pt; }
      .bullet-level-0 { margin-left:0mm; }
      .bullet-level-1 { margin-left:10mm; }
      .bullet-level-2 { margin-left:20mm; }
      .bullet-level-3 { margin-left:30mm; }
      .chapter-title { font-size:16pt; font-weight:bold; text-transform:uppercase; margin:10mm 0; text-align:center; }
      .section-title { font-size:14pt; font-weight:bold; text-transform:titlecase; margin:8mm 0 4mm 0; }
      .subsection-title { font-size:12pt; font-weight:bold; text-transform:titlecase; margin:6mm 0 3mm 0; }
      .content { text-align:justify; font-size:12pt; }
      .preliminary-page { display:flex; flex-direction:column; justify-content:center; min-height:250mm; }
    </style></head><body>`;

    let currentPage = 1;

    // Cover Page (No page number)
    htmlContent += `<div class="page title-page">`;
    if (docData.collegeName) htmlContent += `<div class="college-info">${docData.collegeName}</div>`;
    if (docData.department) htmlContent += `<div class="college-info">Department: ${docData.department}</div>`;
    htmlContent += `<div class="title">${docData.title}</div>`;
    htmlContent += `<div class="subtitle">${docData.subtitle}</div>`;
    if (docData.studentName) htmlContent += `<div class="college-info" style="margin-top:30mm;">Submitted by:<br/><b>${docData.studentName}</b></div>`;
    if (docData.guideName) htmlContent += `<div class="college-info">Under the guidance of:<br/><b>${docData.guideName}</b></div>`;
    if (docData.year) htmlContent += `<div class="college-info" style="margin-top:20mm;">${docData.year}</div>`;
    htmlContent += `</div>`;
    currentPage++;

    // Inner Page (Same as cover page)
    htmlContent += `<div class="page title-page">`;
    if (docData.collegeName) htmlContent += `<div class="college-info">${docData.collegeName}</div>`;
    if (docData.department) htmlContent += `<div class="college-info">Department: ${docData.department}</div>`;
    htmlContent += `<div class="title">${docData.title}</div>`;
    htmlContent += `<div class="subtitle">${docData.subtitle}</div>`;
    if (docData.studentName) htmlContent += `<div class="college-info" style="margin-top:30mm;">Submitted by:<br/><b>${docData.studentName}</b></div>`;
    if (docData.guideName) htmlContent += `<div class="college-info">Under the guidance of:<br/><b>${docData.guideName}</b></div>`;
    if (docData.year) htmlContent += `<div class="college-info" style="margin-top:20mm;">${docData.year}</div>`;
    htmlContent += `</div>`;
    currentPage++;

    // Preliminary sections (no page numbers)
    const preliminarySections = ['declaration', 'certificate', 'acknowledgement'];
    sections.forEach(section => {
      if (preliminarySections.includes(section.type)) {
        htmlContent += `<div class="page preliminary-page">`;
        htmlContent += `<div class="chapter-title">${section.title}</div>`;
        if (section.content) {
          htmlContent += `<div class="content">${section.content.replace(/\n/g, '<br>')}</div>`;
        }
        htmlContent += `</div>`;
        currentPage++;
      }
    });

    // Abstract (starts page numbering)
    const abstractSection = sections.find(s => s.type === 'abstract');
    if (abstractSection) {
      htmlContent += `<div class="page">`;
      htmlContent += `<div class="chapter-title">${abstractSection.title}</div>`;
      if (abstractSection.content) {
        htmlContent += `<div class="content">${abstractSection.content.replace(/\n/g, '<br>')}</div>`;
      }
      if (settings.includePageNumbers) {
        htmlContent += `<div class="page-number">${sectionPages[abstractSection.id]}</div>`;
        htmlContent += `<div class="department-name">${docData.department}</div>`;
      }
      htmlContent += `</div>`;
      currentPage++;
    }

    // INDEX
    if (settings.includeIndex) {
      htmlContent += `<div class="page"><div class="list-title">INDEX</div><table><thead><tr style="background:#f0f0f0;"><th>Chapter No</th><th>Title No</th><th>Subtitle No</th><th>Title Name</th><th class="text-right">Page No.</th></tr></thead><tbody>${generateIndexHtml()}</tbody></table>`;
      if (settings.includePageNumbers) {
        htmlContent += `<div class="page-number">${listPages.index}</div>`;
        htmlContent += `<div class="department-name">${docData.department}</div>`;
      }
      htmlContent += `</div>`;
      currentPage++;
    }

    // List of Figures
    if (settings.includeFigureList && figures.length > 0) {
      htmlContent += `<div class="page"><div class="list-title">LIST OF FIGURES</div><table><thead><tr><th>Figure No</th><th>Figure Name</th><th class="text-right">Page No.</th></tr></thead><tbody>`;
      figures.forEach(fig => {
        htmlContent += `<tr><td>Fig ${fig.number}</td><td>${fig.name}</td><td class="text-right">${fig.page}</td></tr>`;
      });
      htmlContent += `</tbody></table>`;
      if (settings.includePageNumbers) {
        htmlContent += `<div class="page-number">${listPages.figures}</div>`;
        htmlContent += `<div class="department-name">${docData.department}</div>`;
      }
      htmlContent += `</div>`;
      currentPage++;
    }

    // List of Tables
    if (settings.includeTableList && tables.length > 0) {
      htmlContent += `<div class="page"><div class="list-title">LIST OF TABLES</div><table><thead><tr><th>Table No</th><th>Table Name</th><th class="text-right">Page No.</th></tr></thead><tbody>`;
      tables.forEach(tbl => {
        htmlContent += `<tr><td>Table ${tbl.number}</td><td>${tbl.name}</td><td class="text-right">${tbl.page}</td></tr>`;
      });
      htmlContent += `</tbody></table>`;
      if (settings.includePageNumbers) {
        htmlContent += `<div class="page-number">${listPages.tables}</div>`;
        htmlContent += `<div class="department-name">${docData.department}</div>`;
      }
      htmlContent += `</div>`;
      currentPage++;
    }

    // List of Abbreviations
    if (settings.includeAbbreviations && abbreviations.length > 0) {
      htmlContent += `<div class="page"><div class="list-title">LIST OF ABBREVIATIONS</div><table><thead><tr><th style="width:30%;">Abbreviation</th><th>Meaning</th></tr></thead><tbody>`;
      abbreviations.forEach(abbr => {
        htmlContent += `<tr><td><b>${abbr.abbreviation}</b></td><td>${abbr.meaning}</td></tr>`;
      });
      htmlContent += `</tbody></table>`;
      if (settings.includePageNumbers) {
        htmlContent += `<div class="page-number">${listPages.abbreviations}</div>`;
        htmlContent += `<div class="department-name">${docData.department}</div>`;
      }
      htmlContent += `</div>`;
      currentPage++;
    }

    // Content Sections (Chapters)
    sections.forEach((section) => {
      if (!preliminarySections.includes(section.type) && section.type !== 'abstract') {
        let titleHtml = '';

        if (section.type.startsWith('chapter')) {
          const chapterNum = section.type.replace('chapter', '');
          titleHtml = `<div class="chapter-title">CHAPTER ${chapterNum}<br/>${section.title}</div>`;
        } else {
          titleHtml = `<div class="chapter-title">${section.title}</div>`;
        }

        htmlContent += `<div class="page">${titleHtml}`;
        
        if (section.content) {
          htmlContent += `<div class="content">${section.content.replace(/\n/g, '<br>')}</div>`;
        }
        
        // Render bullet points
        if (section.bulletPoints && section.bulletPoints.length > 0) {
          const numberedBullets = calculateBulletNumbers(section.bulletPoints);
          htmlContent += `<div class="bullet-points">`;
          numberedBullets.forEach(bullet => {
            const indentClass = `bullet-level-${Math.min(bullet.level, 3)}`;
            if (bullet.type === 'number') {
              htmlContent += `<div class="bullet-item ${indentClass}">${bullet.number}. ${bullet.text}</div>`;
            } else {
              htmlContent += `<div class="bullet-item ${indentClass}">• ${bullet.text}</div>`;
            }
          });
          htmlContent += `</div>`;
        }
        
        if (section.tableData && section.tableData.length > 0) {
          htmlContent += `<table class="content-table">`;
          section.tableData.forEach((row, idx) => {
            htmlContent += `<tr>`;
            row.forEach(cell => {
              const tag = idx === 0 ? 'th' : 'td';
              htmlContent += `<${tag}>${cell}</${tag}>`;
            });
            htmlContent += `</tr>`;
          });
          htmlContent += `</table>`;
          
          // Table caption
          const tableNumber = getTableNumber(section.id);
          htmlContent += `<div class="image-caption">Table ${tableNumber}: ${section.tableName || `Table ${tableNumber}`}</div>`;
        }
        
        // Render images
        if (section.images && section.images.length > 0) {
          section.images.forEach((image, index) => {
            const figureNumber = getFigureNumber(section.id, index);
            htmlContent += `<img src="${image.url}" class="content-image" alt="${image.name}"/>`;
            htmlContent += `<div class="image-caption">Fig ${figureNumber}: ${image.name}</div>`;
          });
        }
        
        if (settings.includePageNumbers) {
          htmlContent += `<div class="page-number">${sectionPages[section.id]}</div>`;
          htmlContent += `<div class="department-name">${docData.department}</div>`;
        }
        
        htmlContent += `</div>`;
      }
    });

    htmlContent += `</body></html>`;

    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getTabDescription = () => {
    switch(activeTab) {
      case 'sections':
        return '📝 All required chapters are pre-defined as per guidelines. Add content, images, tables, and bullet points.';
      case 'figures':
        return '🖼️ Automatically generated list of figures from your uploaded images.';
      case 'tables':
        return '📊 Automatically generated list of tables from your created tables.';
      case 'abbreviations':
        return '📚 Define abbreviations and their meanings for quick reference.';
      default:
        return '';
    }
  };

  // Enhanced table rendering for sections
  const renderTableEditor = (section) => {
    if (!section.tableData || section.tableData.length === 0) {
      return (
        <div className="text-center py-4 text-slate-400 text-sm">
          No table data. Click "Add Row" to start creating a table.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto border border-slate-600 rounded-lg">
        <table className="w-full text-xs border-collapse">
          <tbody>
            {section.tableData.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx === 0 ? "bg-slate-600" : "bg-slate-700"}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="border border-slate-500 p-1 min-w-[100px]">
                    <input 
                      type="text" 
                      value={cell} 
                      onChange={(e) => updateTableCell(section.id, rowIdx, cellIdx, e.target.value)}
                      className={`w-full px-2 py-1 rounded text-xs border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        rowIdx === 0 ? 'bg-slate-500 text-white font-bold' : 'bg-slate-600 text-white'
                      }`}
                      placeholder={rowIdx === 0 ? `Header ${cellIdx + 1}` : `Data ${cellIdx + 1}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render bullet points editor
  const renderBulletPoints = (section) => {
    const numberedBullets = calculateBulletNumbers(section.bulletPoints || []);
    
    return (
      <div className="space-y-2">
        {numberedBullets.map((bullet, index) => (
          <div key={bullet.id} className={`flex items-start gap-2 p-2 bg-slate-600 rounded border border-slate-500 ml-${bullet.level * 4}`}>
            <div className="flex items-center gap-1 mt-1">
              <button
                onClick={() => changeBulletLevel(section.id, bullet.id, 'increase')}
                className="text-xs bg-slate-500 hover:bg-slate-400 text-white p-1 rounded"
                disabled={bullet.level >= 3}
              >
                →
              </button>
              <button
                onClick={() => changeBulletLevel(section.id, bullet.id, 'decrease')}
                className="text-xs bg-slate-500 hover:bg-slate-400 text-white p-1 rounded"
                disabled={bullet.level <= 0}
              >
                ←
              </button>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <span className="text-slate-300 text-sm min-w-[30px]">
                {bullet.type === 'number' ? `${bullet.number}.` : '•'}
              </span>
              <input
                type="text"
                value={bullet.text}
                onChange={(e) => updateBulletPoint(section.id, bullet.id, 'text', e.target.value)}
                placeholder="Enter bullet point text..."
                className="flex-1 bg-slate-700 text-white px-2 py-1 rounded text-sm border border-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              onClick={() => deleteBulletPoint(section.id, bullet.id)}
              className="text-red-400 hover:text-red-300 p-1"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Update figures and tables lists when sections change
  React.useEffect(() => {
    updateFiguresList();
    updateTablesList();
  }, [sections]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header and Help Tooltip */}
        <div className="mb-6 bg-slate-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-xl">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">📄 Academic Thesis Generator</h1>
              <p className="text-slate-300 text-lg">Strictly follows university guidelines for project thesis preparation</p>
            </div>
            {showHelpTooltip && (
              <div className="bg-blue-600 text-white p-4 rounded-lg max-w-sm shadow-lg">
                <div className="flex justify-between items-start mb-2">
                  <HelpCircle size={20} />
                  <button onClick={() => setShowHelpTooltip(false)} className="text-white hover:text-gray-200">
                    <X size={16} />
                  </button>
                </div>
                <p className="text-sm leading-relaxed">
                  <strong>Guideline Compliance:</strong><br/>
                  • All required chapters pre-defined<br/>
                  • Strict formatting rules applied<br/>
                  • Automatic page numbering<br/>
                  • Department name in footer
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <button onClick={() => setShowSettings(!showSettings)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg text-sm font-medium shadow-lg transition-all hover:scale-105">
            <Settings size={18} /> Page Settings
          </button>
          <button onClick={() => setPreview(!preview)} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-3 rounded-lg text-sm font-medium shadow-lg transition-all hover:scale-105">
            {preview ? <Eye size={18} /> : <EyeOff size={18} />}
            {preview ? 'Hide' : 'Show'} Preview
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded-lg text-sm font-medium shadow-lg transition-all hover:scale-105">
            <Download size={18} /> Export as PDF
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-slate-700 rounded-lg p-6 mb-6 border border-slate-600 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">⚙️ Page Settings (Guideline Compliant)</h2>
              <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Page Margins (Strictly as per guidelines)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Top Margin (1")</label>
                    <input type="number" value={settings.marginTop} onChange={(e) => setSettings({ ...settings, marginTop: parseInt(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Bottom Margin (1.25")</label>
                    <input type="number" value={settings.marginBottom} onChange={(e) => setSettings({ ...settings, marginBottom: parseInt(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Left Margin (1.5")</label>
                    <input type="number" value={settings.marginLeft} onChange={(e) => setSettings({ ...settings, marginLeft: parseInt(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Right Margin (1")</label>
                    <input type="number" value={settings.marginRight} onChange={(e) => setSettings({ ...settings, marginRight: parseInt(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Typography (Times New Roman)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Font Size (12pt)</label>
                    <input type="number" value={settings.fontSize} onChange={(e) => setSettings({ ...settings, fontSize: parseInt(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm mb-1 block">Font Family</label>
                    <select value={settings.fontFamily} onChange={(e) => setSettings({ ...settings, fontFamily: e.target.value })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled>
                      <option>Times New Roman</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-slate-300 text-sm mb-1 block">Line Spacing (1.5)</label>
                  <input type="number" value={settings.lineSpacing} onChange={(e) => setSettings({ ...settings, lineSpacing: parseFloat(e.target.value) })} className="w-full bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none" disabled />
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  Include in Document
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2 text-slate-300 bg-slate-600 px-3 py-2 rounded cursor-pointer hover:bg-slate-500 transition">
                    <input type="checkbox" checked={settings.includePageNumbers} onChange={(e) => setSettings({ ...settings, includePageNumbers: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">Page Numbers</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-300 bg-slate-600 px-3 py-2 rounded cursor-pointer hover:bg-slate-500 transition">
                    <input type="checkbox" checked={settings.includeIndex} onChange={(e) => setSettings({ ...settings, includeIndex: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">INDEX Page</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-300 bg-slate-600 px-3 py-2 rounded cursor-pointer hover:bg-slate-500 transition">
                    <input type="checkbox" checked={settings.includeFigureList} onChange={(e) => setSettings({ ...settings, includeFigureList: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">List of Figures</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-300 bg-slate-600 px-3 py-2 rounded cursor-pointer hover:bg-slate-500 transition">
                    <input type="checkbox" checked={settings.includeTableList} onChange={(e) => setSettings({ ...settings, includeTableList: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">List of Tables</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-300 bg-slate-600 px-3 py-2 rounded cursor-pointer hover:bg-slate-500 transition">
                    <input type="checkbox" checked={settings.includeAbbreviations} onChange={(e) => setSettings({ ...settings, includeAbbreviations: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">Abbreviations</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Information */}
        <div className="bg-slate-800 rounded-lg p-5 mb-6 border border-slate-700 shadow-lg">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText size={20} className="text-blue-500" />
            Document Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-slate-300 text-sm mb-2 block font-medium">Select College</label>
              <select 
                value={docData.collegeName} 
                onChange={(e) => {
                  const selected = collegeTemplates.find(t => t.name === e.target.value);
                  setDocData({ ...docData, collegeName: selected?.value === 'custom' ? '' : e.target.value });
                }}
                className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                {collegeTemplates.map(template => (
                  <option key={template.value} value={template.name}>{template.name}</option>
                ))}
              </select>
            </div>

            {(!docData.collegeName || docData.collegeName === 'Custom') && (
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">College Name (Custom)</label>
                <input 
                  type="text" 
                  value={docData.collegeName === 'Custom' ? '' : docData.collegeName} 
                  onChange={(e) => setDocData({ ...docData, collegeName: e.target.value })} 
                  placeholder="Enter your college name..." 
                  className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm" 
                />
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Document Title (UPPERCASE)</label>
                <input type="text" value={docData.title} onChange={(e) => setDocData({ ...docData, title: e.target.value.toUpperCase() })} placeholder="DOCUMENT TITLE IN UPPERCASE" className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm uppercase" />
              </div>
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Subtitle (UPPERCASE)</label>
                <input type="text" value={docData.subtitle} onChange={(e) => setDocData({ ...docData, subtitle: e.target.value.toUpperCase() })} placeholder="PROJECT THESIS" className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm uppercase" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Department</label>
                <input type="text" value={docData.department} onChange={(e) => setDocData({ ...docData, department: e.target.value })} placeholder="e.g., EE, CSE, IT" className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm" />
              </div>
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Academic Year</label>
                <input type="number" value={docData.year} onChange={(e) => setDocData({ ...docData, year: e.target.value })} placeholder="2024" className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Student Name</label>
                <input type="text" value={docData.studentName} onChange={(e) => setDocData({ ...docData, studentName: e.target.value })} placeholder="Enter student name..." className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm" />
              </div>
              <div>
                <label className="text-slate-300 text-sm mb-2 block font-medium">Guide Name</label>
                <input type="text" value={docData.guideName} onChange={(e) => setDocData({ ...docData, guideName: e.target.value })} placeholder="Enter guide/supervisor name..." className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4 flex-wrap bg-slate-800 p-2 rounded-lg border border-slate-700">
          {[
            { id: 'sections', label: 'Sections', icon: FileText, count: sections.length },
            { id: 'figures', label: 'Figures', icon: Image, count: figures.length },
            { id: 'tables', label: 'Tables', icon: Table2, count: tables.length },
            { id: 'abbreviations', label: 'Abbreviations', icon: BookOpen, count: abbreviations.length }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg scale-105' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                <Icon size={18} />
                {tab.label}
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-blue-500' : 'bg-slate-600'}`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 mb-4">
          <p className="text-slate-300 text-sm">{getTabDescription()}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700 shadow-lg">
              <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
                <h2 className="text-sm font-bold text-white uppercase">{activeTab}</h2>
              </div>
              <div className="p-4 max-h-[calc(100vh-400px)] overflow-y-auto">
                {activeTab === 'sections' && (
                  <>
                    {sections.map((section, index) => (
                      <div key={section.id} className="mb-6 bg-slate-700 rounded-lg p-4 border border-slate-600 hover:border-blue-500 transition">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">
                            {section.type.startsWith('chapter') ? `CH ${section.type.replace('chapter', '')}` : section.type.toUpperCase()}
                          </span>
                          <span className="text-white text-sm font-bold flex-1">{section.title}</span>
                        </div>

                        <textarea 
                          value={section.content} 
                          onChange={(e) => updateSection(section.id, 'content', e.target.value)} 
                          placeholder={`Enter ${section.title.toLowerCase()} content here...`} 
                          className="w-full bg-slate-600 text-white px-3 py-2 rounded h-32 text-sm border border-slate-500 focus:border-blue-500 focus:outline-none resize-none mb-3" 
                        />

                        {/* Bullet Points Section */}
                        <div className="mb-4">
                          <label className="text-slate-300 text-xs mb-2 block flex items-center gap-2">
                            <List size={14} />
                            Bullet Points & Numbered Lists
                          </label>
                          
                          <div className="flex gap-2 mb-2">
                            <button 
                              onClick={() => addBulletPoint(section.id, 'bullet')}
                              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <List size={12} /> Add Bullet
                            </button>
                            <button 
                              onClick={() => addBulletPoint(section.id, 'number')}
                              className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <ListOrdered size={12} /> Add Numbered
                            </button>
                          </div>
                          
                          {renderBulletPoints(section)}
                        </div>

                        {/* Image Upload Section */}
                        <div className="mb-4">
                          <label className="text-slate-300 text-xs mb-2 block flex items-center gap-2">
                            <Image size={14} />
                            Upload Images
                          </label>
                          
                          <div className="flex items-center gap-2 mb-3">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(section.id, e)}
                              className="hidden"
                              ref={el => fileInputRefs.current[section.id] = el}
                            />
                            <button
                              onClick={() => fileInputRefs.current[section.id]?.click()}
                              className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm transition"
                            >
                              <Upload size={14} /> Browse Image
                            </button>
                            <span className="text-slate-400 text-xs">
                              Auto-numbered: Fig {getFigureNumber(section.id, (section.images || []).length)}
                            </span>
                          </div>

                          {/* Display uploaded images */}
                          <div className="space-y-3">
                            {(section.images || []).map((image, imgIndex) => (
                              <div key={image.id} className="bg-slate-600 rounded-lg p-3 border border-slate-500">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-white text-sm font-bold">
                                    Fig {getFigureNumber(section.id, imgIndex)}
                                  </span>
                                  <button 
                                    onClick={() => deleteImage(section.id, image.id)}
                                    className="bg-red-600 hover:bg-red-700 text-white p-1 rounded transition"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                
                                <div className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={image.name}
                                    onChange={(e) => updateImageName(section.id, image.id, e.target.value)}
                                    placeholder="Figure name/caption..."
                                    className="flex-1 bg-slate-500 text-white px-3 py-2 rounded text-sm border border-slate-400 focus:border-blue-500 focus:outline-none"
                                  />
                                </div>
                                
                                <div className="bg-slate-500 rounded p-2">
                                  <img 
                                    src={image.url} 
                                    alt={image.name} 
                                    className="max-w-full h-32 object-contain rounded mx-auto"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Enhanced Table Editor */}
                        <div className="mb-3">
                          <label className="text-slate-300 text-xs mb-2 block flex items-center gap-2">
                            <Table2 size={14} />
                            Table Editor (Auto-numbered: Table {getTableNumber(section.id)})
                          </label>
                          
                          <div className="mb-2">
                            <input
                              type="text"
                              value={section.tableName || ''}
                              onChange={(e) => updateSection(section.id, 'tableName', e.target.value)}
                              placeholder="Table name/caption..."
                              className="w-full bg-slate-600 text-white px-3 py-2 rounded text-sm border border-slate-500 focus:border-blue-500 focus:outline-none mb-2"
                            />
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-2">
                            <button 
                              onClick={() => addTableRow(section.id)}
                              className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <Rows size={12} /> Add Row
                            </button>
                            <button 
                              onClick={() => removeTableRow(section.id)}
                              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <Rows size={12} /> Remove Row
                            </button>
                            <button 
                              onClick={() => addTableColumn(section.id)}
                              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <Columns size={12} /> Add Column
                            </button>
                            <button 
                              onClick={() => removeTableColumn(section.id)}
                              className="flex items-center gap-1 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <Columns size={12} /> Remove Column
                            </button>
                            <button 
                              onClick={() => clearTable(section.id)}
                              className="flex items-center gap-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs transition"
                            >
                              <Trash2 size={12} /> Clear Table
                            </button>
                          </div>
                          
                          {renderTableEditor(section)}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'figures' && (
                  <>
                    {figures.map((fig, index) => (
                      <div key={fig.id} className="mb-3 bg-slate-700 rounded-lg p-3 border border-slate-600 hover:border-blue-500 transition">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-white text-sm font-bold flex items-center gap-2">
                            <Image size={16} className="text-blue-500" />
                            Figure {fig.number}
                          </span>
                        </div>
                        <div className="text-slate-300 text-sm mb-2">{fig.name}</div>
                        <div className="flex items-center gap-2 text-slate-400 text-xs">
                          <span>Page: {fig.page}</span>
                        </div>
                      </div>
                    ))}
                    {figures.length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <Image size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No figures added yet. Upload images in sections to see them here.</p>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'tables' && (
                  <>
                    {tables.map((tbl, index) => (
                      <div key={tbl.id} className="mb-3 bg-slate-700 rounded-lg p-3 border border-slate-600 hover:border-blue-500 transition">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-white text-sm font-bold flex items-center gap-2">
                            <Table2 size={16} className="text-green-500" />
                            Table {tbl.number}
                          </span>
                        </div>
                        <div className="text-slate-300 text-sm mb-2">{tbl.name}</div>
                        <div className="flex items-center gap-2 text-slate-400 text-xs">
                          <span>Page: {tbl.page}</span>
                        </div>
                      </div>
                    ))}
                    {tables.length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <Table2 size={48} className="mx-auto mb-2 opacity-50" />
                        <p>No tables added yet. Create tables in sections to see them here.</p>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'abbreviations' && (
                  <>
                    {abbreviations.map((abbr, index) => (
                      <div key={abbr.id} className="mb-3 bg-slate-700 rounded-lg p-3 border border-slate-600 hover:border-blue-500 transition">
                        <div className="flex items-center gap-2 mb-2">
                          <BookOpen size={16} className="text-purple-500" />
                          <input type="text" value={abbr.abbreviation} onChange={(e) => updateAbbreviation(abbr.id, 'abbreviation', e.target.value)} placeholder="Abbreviation (e.g., IoT)..." className="flex-1 bg-slate-600 text-white px-3 py-2 rounded text-sm border border-slate-500 focus:border-blue-500 focus:outline-none font-bold" />
                          <button onClick={() => deleteAbbreviation(abbr.id)} className="bg-red-600 hover:bg-red-700 text-white p-1 rounded transition">
                            <X size={14} />
                          </button>
                        </div>
                        <input type="text" value={abbr.meaning} onChange={(e) => updateAbbreviation(abbr.id, 'meaning', e.target.value)} placeholder="Full meaning (e.g., Internet of Things)..." className="w-full bg-slate-600 text-white px-3 py-2 rounded text-sm border border-slate-500 focus:border-blue-500 focus:outline-none" />
                      </div>
                    ))}
                    <button onClick={addAbbreviation} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg text-sm font-medium transition">
                      <Plus size={18} /> Add New Abbreviation
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          {preview && (
            <div className="bg-slate-800 rounded-lg overflow-hidden h-[calc(100vh-400px)] border border-slate-700 shadow-lg">
              <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
                <h2 className="text-sm font-bold text-white">📋 GUIDELINE COMPLIANCE CHECK</h2>
              </div>
              <div className="p-4 overflow-y-auto h-full bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="bg-white p-5 rounded-lg shadow-md border border-gray-200">
                  <div className="border-b-2 border-blue-500 pb-3 mb-4">
                    <div className="font-bold text-lg text-gray-800 mb-1">{docData.title}</div>
                    <div className="text-gray-600 text-sm">{docData.subtitle}</div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                      <div className="text-xs font-semibold text-blue-800 mb-1">Document Structure</div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                        <div className="flex items-center gap-1">
                          <FileText size={12} className="text-blue-600" />
                          <span>{sections.length} Required Chapters</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Image size={12} className="text-green-600" />
                          <span>{figures.length} Figures</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Table2 size={12} className="text-purple-600" />
                          <span>{tables.length} Tables</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <BookOpen size={12} className="text-orange-600" />
                          <span>{abbreviations.length} Abbreviations</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-3 rounded border-l-4 border-green-500">
                      <div className="text-xs font-semibold text-green-800 mb-2">Formatting Compliance</div>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div className="flex items-center gap-1">
                          <span className="text-green-600">✓</span>
                          <span>Times New Roman Font</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-green-600">✓</span>
                          <span>12pt Font Size</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-green-600">✓</span>
                          <span>1.5 Line Spacing</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-green-600">✓</span>
                          <span>Strict Margin Settings</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 p-3 rounded border-l-4 border-purple-500">
                      <div className="text-xs font-semibold text-purple-800 mb-2">Required Elements</div>
                      <div className="flex flex-wrap gap-1">
                        {settings.includeIndex && <span className="bg-purple-200 text-purple-800 px-2 py-0.5 rounded text-xs">INDEX</span>}
                        {settings.includeFigureList && <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs">Figures List</span>}
                        {settings.includeTableList && <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded text-xs">Tables List</span>}
                        {settings.includeAbbreviations && <span className="bg-orange-200 text-orange-800 px-2 py-0.5 rounded text-xs">Abbreviations</span>}
                        {settings.includePageNumbers && <span className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs">Page Numbers</span>}
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-500">
                      <div className="text-xs font-semibold text-yellow-800 mb-2">Chapter Structure</div>
                      <div className="space-y-1 text-xs text-gray-700 max-h-20 overflow-y-auto">
                        {sections.slice(0, 8).map((section, idx) => (
                          <div key={section.id} className="flex items-start gap-2">
                            <span className="text-gray-400 text-xs">•</span>
                            <span className="flex-1 truncate">{section.title}</span>
                          </div>
                        ))}
                        {sections.length > 8 && (
                          <div className="text-gray-500 text-xs">+ {sections.length - 8} more chapters</div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <div className="text-xs text-center text-gray-500">
                        ✓ Strictly follows university guidelines
                        <br />✓ Automatic page numbering with department name
                        <br />✓ Ready for hard-bound submission
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-start gap-3">
            <HelpCircle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold text-sm mb-1">University Guideline Compliance</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                <strong>Strict Formatting:</strong> All settings are locked to comply with university guidelines.<br />
                <strong>Required Chapters:</strong> All 20 required chapters are pre-defined and cannot be modified.<br />
                <strong>Page Numbering:</strong> Automatic numbering with department name in footer.<br />
                <strong>Font & Spacing:</strong> Times New Roman, 12pt, 1.5 line spacing enforced.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}